import React, { useEffect, useMemo, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { selectIncident, useAppSelector } from 'slices/commonSelectors';
import dayjs from 'dayjs';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { OperationalPeriod } from 'components/IAP/hooks/incidentHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useExportedFormFiles } from 'components/FileManagement/hooks/useExportedFormFiles';
import useDownloadFile from 'components/FileManagement/hooks/useDownloadFile';
import { toast } from 'react-toastify';
import DocViewer from '@cyntler/react-doc-viewer';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { useSearchParams } from 'react-router';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

type IncidentActionPlanProps = {
  currPeriod?: OperationalPeriod;
  isFuture?: boolean;
};

/**
 * Incident Action Plan (IAP) card, rendered on "Current Period" tab for OP >= 2
 * and "Future Period" tab
 */
const IncidentActionPlan: React.FC<IncidentActionPlanProps> = ({
  currPeriod,
  isFuture = false,
}) => {
  const currentIncident = useAppSelector(selectIncident);
  const { exportPaginatedIAP, previewFromSignedUrl } = useIcsFormPdfPreview();
  const [isGenerating, setIsGenerating] = useState(false);
  const downloadNotebookFile = useDownloadFile();
  const { data: exportedForms } = useExportedFormFiles(currentIncident?.id);
  const { captureMapSketchFile } = useUscgMapContext();
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-incident-action-plan`;
  const [signedUrl, setSignedUrl] = useState<string>();

  const opPeriodNumber = (currPeriod?.period_number ?? 0) + (isFuture ? 1 : 0);

  const resolvedPeriodIapFile = useMemo(() => {
    const exportedIAPPattern = new RegExp('^IAP-OP' + opPeriodNumber + '-');
    return exportedForms
      ?.filter((file) => {
        return file.file_name.match(exportedIAPPattern);
      })
      .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
      .at(0);
  }, [opPeriodNumber, exportedForms]);

  useEffect(() => {
    if (!resolvedPeriodIapFile) return;

    (async () => {
      try {
        const url = await getSignedUrl(resolvedPeriodIapFile.fileUrl);
        setSignedUrl(url);
      } catch (error) {
        console.error('Failed to fetch signed URL:', error);
      }
    })();
  }, [resolvedPeriodIapFile, getSignedUrl]);

  const displayDocuments = useMemo(() => {
    if (!signedUrl || !resolvedPeriodIapFile) return undefined;
    return [{ uri: signedUrl, fileName: resolvedPeriodIapFile.file_name }];
  }, [signedUrl, resolvedPeriodIapFile]);

  const handlePreviewCurrentIAP = async () => {
    if (!resolvedPeriodIapFile) {
      toast.warning('No exported IAP found for the current period.');
      return;
    }

    try {
      const doc = displayDocuments?.[0];
      let urlToUse: string;

      if (doc?.uri) {
        urlToUse = doc.uri;
      } else {
        urlToUse = await getSignedUrl(resolvedPeriodIapFile.fileUrl);
      }

      previewFromSignedUrl({
        fileUrl: urlToUse,
        fileName: resolvedPeriodIapFile.file_name,
        onDownload: () => {
          void downloadNotebookFile(resolvedPeriodIapFile);
        },
      });
    } catch (_error) {
      toast.error('Failed to load preview');
    }
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget w-100"
      contentClassName="content mt-2"
      headerLeft={
        <h3 className="m-0">
          <SharedIcon
            iconName="description"
            classes="me-2"
            size="md"
            color="primary"
          />
          {getIncidentCardTitle('incidentActionPlan')}
          {isFuture ? ' - Future' : ''}
        </h3>
      }
      headerRight={
        isFuture ? (
          <StylishNewButton
            className="me-2"
            disabled={isGenerating}
            onClick={async (e) => {
              e.stopPropagation();
              setIsGenerating(true);
              try {
                const mapSketch = await captureMapSketchFile();
                await exportPaginatedIAP({
                  mapSketch,
                  periodNumber: opPeriodNumber,
                });
              } finally {
                setIsGenerating(false);
              }
            }}
          >
            <SharedIcon iconName="visibility" classes="me-2" size="sm" />
            <span>Generate & Preview Next IAP</span>
          </StylishNewButton>
        ) : (
          <StylishNewButton
            disabled={isGenerating}
            onClick={async (e) => {
              e.stopPropagation();
              setIsGenerating(true);
              try {
                await handlePreviewCurrentIAP();
              } finally {
                setIsGenerating(false);
              }
            }}
            className="button button--primary"
          >
            <SharedIcon iconName="visibility" classes="me-2" size="sm" />
            Preview IAP
          </StylishNewButton>
        )
      }
    >
      {displayDocuments ? (
        <DocViewer documents={displayDocuments} />
      ) : (
        <div className="section-main d-flex flex-column align-items-center">
          <div className="d-flex justify-content-center">
            <SharedIcon iconName="description" classes="me-2" size="lg" />
          </div>
          <div>
            <div className="fs-5 mb-2">
              {getIncidentCardTitle('incidentActionPlan')}
            </div>
            <div className="mb-2">Please Generate or Upload an IAP PDF</div>
          </div>
        </div>
      )}
    </ConditionalCollapsibleCard>
  );
};

export default IncidentActionPlan;
